import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronRight, Home, Share2, Download } from "lucide-react";
import { Button } from "@/components/ui";
import { DocsRenderer } from "../components/DocsRenderer";
import { ShareDocDialog } from "../components/ShareDocDialog";
import { useDocsAuth } from "../hooks/useDocsAuth";
import fullLogo from "@/assets/logos/full-logo.svg";
import type { DocsPageContent } from "../types";

export function DocsPageView() {
  const { id } = useParams<{ id: string }>();
  const { isLoading: authLoading, isInternal } = useDocsAuth();
  const [page, setPage] = useState<DocsPageContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (authLoading || !id) return;

    let cancelled = false;

    async function fetchPage() {
      try {
        const token = localStorage.getItem("docs_jwt");
        const headers: Record<string, string> = {};
        if (token) headers["Authorization"] = `Bearer ${token}`;

        const res = await fetch(`/api/docs/pages/${encodeURIComponent(id!)}`, {
          headers,
        });

        if (!cancelled) {
          if (res.ok) {
            const data = await res.json();
            setPage(data);
          } else if (res.status === 403) {
            setError("You do not have access to this document.");
          } else if (res.status === 404) {
            setError("Document not found.");
          } else if (res.status === 503) {
            const body = await res.json().catch(() => null);
            setError(
              body?.detail ??
                "Docs are temporarily unavailable. Please try again shortly.",
            );
          } else {
            setError("Failed to load document.");
          }
          setLoading(false);
        }
      } catch {
        if (!cancelled) {
          setError("Network error loading document.");
          setLoading(false);
        }
      }
    }

    fetchPage();
    return () => { cancelled = true; };
  }, [id, authLoading]);

  if (loading || authLoading) {
    return (
      <div className="space-y-4">
        <div className="h-6 w-48 animate-pulse rounded bg-ev-surface-subtle" />
        <div className="h-8 w-96 animate-pulse rounded bg-ev-surface-subtle" />
        <div className="h-4 w-full animate-pulse rounded bg-ev-surface-subtle" />
        <div className="h-4 w-3/4 animate-pulse rounded bg-ev-surface-subtle" />
        <div className="h-4 w-5/6 animate-pulse rounded bg-ev-surface-subtle" />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <Link
          to="/docs"
          className="mb-4 inline-flex items-center gap-1 text-sm text-ev-info-text hover:underline"
        >
          <Home className="h-3.5 w-3.5" /> Back to documents
        </Link>
        <div className="rounded-ev border border-ev-danger-border bg-ev-danger-bg p-4 text-sm text-ev-danger-text">
          {error}
        </div>
      </div>
    );
  }

  if (!page) return null;

  return (
    <div>
      {/* Breadcrumb */}
      <nav className="mb-6 flex items-center gap-1.5 text-sm text-ev-text-muted">
        <Link to="/docs" className="hover:text-ev-info-text">
          <Home className="h-3.5 w-3.5" />
        </Link>
        <ChevronRight className="h-3.5 w-3.5" />
        <span className="font-medium text-ev-text-primary">{page.title}</span>
      </nav>

      {/* Title + actions */}
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ev-text-primary">{page.title}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {isInternal &&
              page.labels.map((label) => (
                <span
                  key={label}
                  className="inline-block rounded-full bg-ev-surface-subtle px-2.5 py-0.5 text-xs font-medium uppercase tracking-wide text-ev-text-muted print:hidden"
                >
                  {label}
                </span>
              ))}
            {page.last_updated && (
              <span className="text-xs text-ev-text-muted">
                Updated {new Date(page.last_updated).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>

        {/* Action buttons — Share is internal-only (external users shouldn't
            generate more magic links); Download is available to everyone
            because external customers may want an offline copy. */}
        <div className="flex items-center gap-2 print:hidden">
          {isInternal && (
            <ShareDocDialog pageId={id!} pageTitle={page.title} scope="page">
              {(open) => (
                <Button variant="outline" onClick={open}>
                  <Share2 className="mr-1.5 h-4 w-4" />
                  Share
                </Button>
              )}
            </ShareDocDialog>
          )}
          <Button
            variant="ghost"
            aria-label="Download as PDF"
            onClick={() => {
              const dateStr = new Date().toISOString().slice(0, 10);
              const originalTitle = document.title;
              document.title = `${page.title} - ${dateStr}`;
              window.print();
              setTimeout(() => { document.title = originalTitle; }, 1000);
            }}
          >
            <Download className="mr-1.5 h-4 w-4" />
            Download
          </Button>
        </div>
      </div>

      {/* Print-only header — visible only when printing/saving as PDF */}
      <div className="hidden print:block mb-8 pb-4 border-b border-gray-300">
        <div className="flex items-center justify-between">
          <img src={fullLogo} alt="evermore" className="h-16" />
          <div className="text-right text-xs text-gray-500">
            <div className="font-medium text-gray-700">{page.title}</div>
            {page.last_updated && (
              <div>Version date: {new Date(page.last_updated).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</div>
            )}
          </div>
        </div>
      </div>

      {/* Rendered markdown content */}
      <DocsRenderer content={page.content} />

      {/* Print-only footer */}
      <div className="hidden print:block mt-8 pt-4 border-t border-gray-300 text-xs text-gray-400 text-center">
        evermore outcomes &middot; {page.title} &middot; {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
      </div>

      {/* Download button moved to header action buttons */}
    </div>
  );
}
